import {
  FeatureFlag,
  useIsFeatureFlagEnabled,
} from '@fabric-app/business/feature-flags/feature-flags.store';
import { DropdownMenu } from '@fabric-app/ui/components/DropdownMenu/DropdownMenu';
import { Flex } from '@fabric-app/ui/components/Flex';
import { P } from '@fabric-app/ui/components/Typography';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { newArrayLength } from '@fabric-app/utils/array';
import { useState } from 'react';
import { ZoomSliderWithinDD } from '../ZoomSlider/ZoomSliderWithinDropdown';
import { TextInput } from '@fabric-app/ui/components/TextInput/TextInput';

/**
 * 0.5-0.999
 */
const recencyDecaySteps = newArrayLength(500).map((_, i) => (i + 500) / 1000);

/**
 * 0-1
 */
const zeroToOneSteps = newArrayLength(101).map((_, i) => i / 100);

/**
 * 0-2
 */
const zeroToTwoSteps = newArrayLength(201).map((_, i) => i / 100);

const CONFIG = {
  recencyDecay: {
    title: 'Recency decay',
    min: Math.min(...recencyDecaySteps),
    max: Math.max(...recencyDecaySteps),
    steps: recencyDecaySteps,
    defaultValue: Math.max(...recencyDecaySteps),
  },
  precision: {
    title: 'Precision',
    min: Math.min(...zeroToOneSteps),
    max: Math.max(...zeroToOneSteps),
    steps: zeroToOneSteps,
    defaultValue: 0,
  },
  partialMatchWeight: {
    title: 'Partial match weight',
    min: Math.min(...zeroToOneSteps),
    max: Math.max(...zeroToOneSteps),
    steps: zeroToOneSteps,
    defaultValue: 0.1,
  },
  metaWeight: {
    title: 'Meta weight',
    min: Math.min(...zeroToTwoSteps),
    max: Math.max(...zeroToTwoSteps),
    steps: zeroToTwoSteps,
    defaultValue: 0.1,
  },
  titleWeight: {
    title: 'Title weight',
    min: Math.min(...zeroToTwoSteps),
    max: Math.max(...zeroToTwoSteps),
    steps: zeroToTwoSteps,
    defaultValue: 1.5,
  },
};

type ConfigKey = keyof typeof CONFIG;

export const useAdminSettings = () => {
  const [state, setState] = useState<Record<ConfigKey, number>>({
    recencyDecay: CONFIG.recencyDecay.defaultValue,
    precision: CONFIG.precision.defaultValue,
    partialMatchWeight: CONFIG.partialMatchWeight.defaultValue,
    metaWeight: CONFIG.metaWeight.defaultValue,
    titleWeight: CONFIG.titleWeight.defaultValue,
  });

  const setStateForKey = (key: keyof typeof CONFIG) => (value: number) => {
    setState((prev) => ({ ...prev, [key]: value }));
  };

  const isEnabled = useIsFeatureFlagEnabled(FeatureFlag.SEARCH_PARAM_CUSTOMIZATION);

  if (!isEnabled) {
    return {
      isEnabled,
      searchArgs: {},
      dropdownControls: null,
    };
  }

  return {
    isEnabled,
    searchArgs: state,
    dropdownControls: (
      <DropdownMenu.Group>
        {Object.entries(CONFIG).map(([_l, config]) => {
          const key = _l as ConfigKey;
          const setValue = setStateForKey(key);
          return (
            <Flex
              key={key}
              alignItems="center"
              direction="column"
              style={{
                background: cssVar['color-bg-tertiary'],
                borderRadius: 6,
                padding: 8,
              }}
            >
              <P size="sm" weight={500}>
                {config.title}
              </P>
              <div style={{ width: '100%' }}>
                <ZoomSliderWithinDD
                  onChange={setValue}
                  value={state[key]}
                  steps={config.steps}
                  formatter={(value) => `${value}`}
                />
              </div>
              <TextInput
                type="number"
                max={config.max}
                min={config.min}
                value={state[key]}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  if (value > config.max) {
                    setValue(config.max);
                  } else if (value < config.min) {
                    setValue(config.min);
                  } else {
                    setValue(value);
                  }
                }}
              />
            </Flex>
          );
        })}
      </DropdownMenu.Group>
    ),
  };
};
